// Lab 4 safety invariants: unique shard ownership, quiescent agreement with
// committed configs, correct-config service, no lost or duplicated keys,
// transaction atomicity, lock release, at-most-once application, config
// compatibility, and client result correspondence.
function masterConfigs(state) {
  const master = state.nodes[state.config.master];
  return master ? master.durable.configs : [];
}

export function uniqueOwnership(state) {
  for (const cfg of masterConfigs(state)) {
    for (const s of state.config.shards) {
      const owner = cfg.shards[s];
      if (!owner || !cfg.groups.includes(owner)) {
        return {
          invariant: 'lab4-unique-ownership',
          message: 'config ' + cfg.n + ' leaves shard ' + s + ' without exactly one owner',
        };
      }
    }
  }
  return null;
}

function isQuiescent(durable) {
  if (durable.transfersOut.length > 0 || durable.transfersIn.length > 0) return false;
  if (durable.pendingReconfig || durable.pendingAnnounce) return false;
  return !Object.values(durable.txns).some((t) =>
    ['PREPARING', 'PREPARED', 'COMMITTING', 'ABORTING', 'EARLY_COMMIT'].includes(t.state)
  );
}

export function quiescentAgreement(state) {
  const configs = {};
  masterConfigs(state).forEach((c) => {
    configs[c.n] = c;
  });
  for (const id of [...state.config.groups, ...state.config.idleGroups]) {
    const node = state.nodes[id];
    if (!node || !isQuiescent(node.durable)) continue;
    const cfg = configs[node.durable.configN];
    if (!cfg && node.durable.configN !== 0) {
      return { invariant: 'lab4-quiescent-agreement', message: id + ' serves unknown config' };
    }
    if (node.durable.configN === 0) continue;
    const want = Object.keys(cfg.shards)
      .filter((s) => cfg.shards[s] === id)
      .map(Number)
      .sort();
    const have = Object.keys(node.durable.shards).map(Number).sort();
    if (JSON.stringify(want) !== JSON.stringify(have)) {
      return {
        invariant: 'lab4-quiescent-agreement',
        message: id + ' holds shards ' + JSON.stringify(have) + ' but config ' + cfg.n + ' assigns ' + JSON.stringify(want),
      };
    }
  }
  return null;
}

export function serveCorrectConfig(state) {
  const configs = {};
  masterConfigs(state).forEach((c) => {
    configs[c.n] = c;
  });
  for (const id of [...state.config.groups, ...state.config.idleGroups]) {
    const node = state.nodes[id];
    if (!node) continue;
    const bad = node.durable.served.find((s) => {
      const cfg = configs[s.configN];
      return !cfg || cfg.shards[s.shard] !== id;
    });
    if (bad) {
      return {
        invariant: 'lab4-serve-config',
        message: id + ' served shard ' + bad.shard + ' outside config ' + bad.configN,
      };
    }
  }
  return null;
}

export function keysPlacedOnce(state) {
  const inFlight = new Set();
  state.events.forEach((e) => {
    if (
      e.status === 'pending' &&
      e.payload &&
      (e.payload.msgType === 'move' || e.payload.msgType === 'moveack')
    ) {
      inFlight.add(e.payload.shard);
    }
  });
  const locations = {};
  [...state.config.groups, ...state.config.idleGroups].forEach((id) => {
    const node = state.nodes[id];
    if (!node) return;
    Object.keys(node.durable.shards).forEach((s) => {
      Object.keys(node.durable.shards[s].keys).forEach((k) => {
        locations[k] = locations[k] || [];
        locations[k].push({ group: id, shard: Number(s) });
      });
    });
  });
  const bad = Object.keys(locations).find((k) => {
    const locs = locations[k];
    if (locs.length <= 1) return false;
    return !locs.some((l) => inFlight.has(l.shard));
  });
  if (bad) {
    return {
      invariant: 'lab4-key-placement',
      message: 'key ' + bad + ' lives in ' + locations[bad].length + ' groups with no move in flight',
    };
  }
  return null;
}

export function transactionAtomicity(state) {
  // Only settled transactions are judged: a commit in flight legitimately
  // shows COMMITTED on one group before the others.
  const inFlight = new Set();
  state.events.forEach((e) => {
    if (e.status === 'pending' && e.payload && e.payload.txnId) inFlight.add(e.payload.txnId);
  });
  const records = {};
  [...state.config.groups, ...state.config.idleGroups].forEach((id) => {
    const node = state.nodes[id];
    if (!node) return;
    Object.values(node.durable.txns).forEach((t) => {
      records[t.id] = records[t.id] || {};
      records[t.id][id] = t.state;
    });
  });
  const bad = Object.keys(records).find((id) => {
    if (inFlight.has(id)) return false;
    const states = records[id];
    const groups = Object.keys(states);
    if (!groups.some((g) => states[g] === 'COMMITTED')) return false;
    return groups.some((g) => states[g] !== 'COMMITTED' && states[g] !== undefined);
  });
  if (bad) {
    return { invariant: 'lab4-atomicity', message: 'txn ' + bad + ' committed on some groups only' };
  }
  return null;
}

export function locksReleased(state) {
  for (const id of [...state.config.groups, ...state.config.idleGroups]) {
    const node = state.nodes[id];
    if (!node) continue;
    const terminal = new Set(
      Object.values(node.durable.txns)
        .filter((t) => t.state === 'COMMITTED' || t.state === 'ABORTED')
        .map((t) => t.id)
    );
    const bad = Object.keys(node.durable.locks).find((k) => terminal.has(node.durable.locks[k]));
    if (bad) {
      return {
        invariant: 'lab4-lock-release',
        message: id + ' still locks ' + bad + ' for a settled txn',
      };
    }
  }
  return null;
}

export function atMostOncePerGroup(state) {
  for (const id of [...state.config.groups, ...state.config.idleGroups]) {
    const node = state.nodes[id];
    if (!node) continue;
    const seen = new Set();
    for (const x of node.durable.appliedIds) {
      if (seen.has(x)) {
        return { invariant: 'lab4-at-most-once', message: id + ' applied ' + x + ' twice' };
      }
      seen.add(x);
    }
  }
  return null;
}

export function configCompatibility(state) {
  const configs = {};
  [...state.config.groups, ...state.config.idleGroups].forEach((id) => {
    const node = state.nodes[id];
    if (!node) return;
    Object.values(node.durable.txns).forEach((t) => {
      configs[t.id] = configs[t.id] || new Set();
      configs[t.id].add(t.configN);
    });
  });
  const bad = Object.keys(configs).find((id) => configs[id].size > 1);
  if (bad) {
    return { invariant: 'lab4-config-compatibility', message: 'txn ' + bad + ' spans configs' };
  }
  return null;
}

export function clientResultCorrespondence(state) {
  const applied = new Set();
  [...state.config.groups, ...state.config.idleGroups].forEach((id) => {
    const node = state.nodes[id];
    if (!node) return;
    node.durable.appliedIds.forEach((x) => {
      const at = x.lastIndexOf('@');
      applied.add(at >= 0 ? x.slice(0, at) : x);
    });
  });
  for (const c of state.config.clients) {
    const node = state.nodes[c];
    if (!node) continue;
    const orphan = node.durable.completed.find(
      (r) => r.kind !== 'admin' && !applied.has(c + '#' + r.seq)
    );
    if (orphan) {
      return {
        invariant: 'lab4-result-correspondence',
        message: 'client ' + c + ' completed seq ' + orphan.seq + ' no group applied',
      };
    }
  }
  return null;
}

export function checkLab4Invariants(state) {
  return [
    uniqueOwnership,
    quiescentAgreement,
    serveCorrectConfig,
    keysPlacedOnce,
    transactionAtomicity,
    locksReleased,
    atMostOncePerGroup,
    configCompatibility,
    clientResultCorrespondence,
  ]
    .map((fn) => fn(state))
    .filter(Boolean);
}
